import { useState, useEffect, useCallback } from 'react'
import {
  Page,
  Layout,
  Card,
  Select,
  Text,
  Button,
  Modal,
  TextField,
  Banner,
  BlockStack,
  InlineStack,
  Box,
  Divider,
} from '@shopify/polaris'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, PreviewResponse } from '../api/client'
import { useShopifyAuth } from '../hooks/useShopifyAuth'
import SMSMockup from '../components/SMSMockup'

export default function Dashboard() {
  // Initialize Shopify auth
  useShopifyAuth()

  const queryClient = useQueryClient()

  // SMS setup modal state (must complete before using app)
  const [showSmsSetup, setShowSmsSetup] = useState(false)
  const [smsSetupStep, setSmsSetupStep] = useState(1)
  useEffect(() => {
    if (!localStorage.getItem('otty_sms_setup_done')) {
      setShowSmsSetup(true)
    }
  }, [])
  const dismissSmsSetup = () => {
    localStorage.setItem('otty_sms_setup_done', 'true')
    setShowSmsSetup(false)
  }

  // Get shop domain for direct admin links
  const shopDomain = window.shopify?.config?.shop || ''
  const checkoutSettingsUrl = shopDomain
    ? `https://admin.shopify.com/store/${shopDomain.replace('.myshopify.com', '')}/settings/checkout`
    : ''

  // Welcome banner state (first-time users)
  const [showWelcome, setShowWelcome] = useState(false)
  useEffect(() => {
    if (!localStorage.getItem('otty_welcome_dismissed')) {
      setShowWelcome(true)
    }
  }, [])
  const dismissWelcome = () => {
    localStorage.setItem('otty_welcome_dismissed', 'true')
    setShowWelcome(false)
  }

  // Local state for form
  const [maxDiscount, setMaxDiscount] = useState(15)
  const [offerTypes, setOfferTypes] = useState<string[]>(['percentage_off', 'free_shipping'])
  const [showBrandVoiceModal, setShowBrandVoiceModal] = useState(false)

  // Brand voice fields
  const [brandVoice, setBrandVoice] = useState({
    summary: '',
    tone: '',
    personality: '',
    writingStyle: '',
    messagingGuidelines: '',
    keywordsToUse: '',
    avoidPatterns: '',
    cartRecoveryExamples: '',
  })
  const [editingBrandVoice, setEditingBrandVoice] = useState({
    summary: '',
    tone: '',
    personality: '',
    writingStyle: '',
    messagingGuidelines: '',
    keywordsToUse: '',
    avoidPatterns: '',
    cartRecoveryExamples: '',
  })

  // Preview state - with mock default
  const [preview, setPreview] = useState<PreviewResponse | null>({
    message: "Alex, your selections are still waiting — timeless pieces crafted to catch the light and hold meaning. As a thank you for considering Aurrora, enjoy 9.7% off with code GLOW97. Beauty like this is worth returning to. [Link]",
    offer_description: "9.7% Off",
    coupon_code: "GLOW97",
  })

  // Check if we're in preview mode (not in Shopify Admin)
  const isPreviewMode = !window.shopify

  // Mock data for UI preview (remove when backend is ready)
  const mockConfig = {
    max_discount_percent: 15,
    offer_types: {
      percentage_off: true,
      free_shipping: true,
    },
    brand_voice: {
      summary: "Aurrora is an elegant, modern jewelry brand inspired by light, emotion, and timeless beauty. The brand speaks with quiet confidence—refined, warm, and intentional—making customers feel like they're discovering something meaningful, not being sold to.",
      tone: "Soft, elegant, and confident with a sense of calm luxury. Uses simple, graceful language that feels thoughtful and reassuring rather than flashy or sales-driven.",
      personality: "Refined and trustworthy, like a knowledgeable stylist who understands subtle beauty. Emotionally aware, detail-oriented, and focused on helping customers feel special, seen, and confident.",
      writingStyle: "Minimal and polished. Short, flowing sentences with an emphasis on emotion and imagery. Avoids hype, slang, and hard selling. Prioritizing clarity, softness, and timeless appeal. Occasional poetic phrasing, never emojis.",
      messagingGuidelines: "Lead with emotion and meaning, not urgency. Make the customer feel elevated and confident in their choice. Keep copy concise and intentional. Include a clear but gentle call-to-action. Focus on craftsmanship, symbolism, and everyday wearability.",
      keywordsToUse: "timeless, elegant, luminous, crafted, refined, meaningful, everyday luxury, glow, subtle, intentional, heirloom-inspired",
      avoidPatterns: "Aggressive urgency, discount language, excessive exclamation points, slang, trend-heavy phrasing, long paragraphs, or anything that feels mass-market or pushy.",
      cartRecoveryExamples: "Your selections are waiting — timeless pieces crafted to catch the light and hold meaning. A gentle reminder that beauty like this is worth returning to.\n\nSomething you loved is still here. Refined, luminous, and ready to become part of your story.",
    },
    brand_voice_tone: "Aurrora",
    brand_voice_style: "elegant",
    example_message: "Alex, your selections are still waiting — timeless pieces crafted to catch the light and hold meaning. As a thank you for considering Aurrora, enjoy 9.7% off with code GLOW97. Beauty like this is worth returning to. [Link]",
    shop_name: "Aurrora",
  }

  // Fetch config - disabled in preview mode, uses mock data instead
  const { data: apiConfig, isLoading, error } = useQuery({
    queryKey: ['config'],
    queryFn: api.getConfig,
    enabled: !isPreviewMode, // Only fetch when in Shopify Admin
  })

  // Use API config or mock config
  const config = isPreviewMode ? mockConfig : apiConfig

  // Update config mutation
  const updateConfigMutation = useMutation({
    mutationFn: api.updateConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config'] })
    },
  })

  // Generate preview mutation
  const previewMutation = useMutation({
    mutationFn: api.generatePreview,
    onSuccess: (data) => {
      setPreview(data)
    },
    onError: () => {
      // Keep showing mock preview on error
      console.log('Preview API not available, using mock data')
    },
  })

  // Initialize form from config
  useEffect(() => {
    if (config) {
      setMaxDiscount(config.max_discount_percent)
      const types: string[] = []
      if (config.offer_types.percentage_off) types.push('percentage_off')
      if (config.offer_types.free_shipping) types.push('free_shipping')
      setOfferTypes(types)
      if (config.brand_voice) {
        setBrandVoice({
          summary: config.brand_voice.summary || '',
          tone: config.brand_voice.tone || '',
          personality: config.brand_voice.personality || '',
          writingStyle: config.brand_voice.writingStyle || '',
          messagingGuidelines: config.brand_voice.messagingGuidelines || '',
          keywordsToUse: config.brand_voice.keywordsToUse || '',
          avoidPatterns: config.brand_voice.avoidPatterns || '',
          cartRecoveryExamples: config.brand_voice.cartRecoveryExamples || '',
        })
      }
      // Use example_message from backend if available (generated during onboarding)
      if (config.example_message) {
        setPreview({
          message: config.example_message,
          offer_description: "9.7% Off",
          coupon_code: "AUTO-APPLIED",
        })
      }
    }
  }, [config])

  // Generate preview when settings change
  const generatePreview = useCallback(() => {
    // If both are enabled, AI will decide which to use or combine them
    const offerType = offerTypes.includes('percentage_off') && offerTypes.includes('free_shipping')
      ? 'combined'
      : offerTypes.includes('percentage_off')
      ? 'percentage_off'
      : 'free_shipping'

    previewMutation.mutate({
      max_discount_percent: maxDiscount,
      offer_type: offerType,
      cart_value: 89.99,
      customer_name: 'Sarah',
    })
  }, [maxDiscount, offerTypes, previewMutation])

  // NOTE: Preview is generated ONCE during onboarding and stored as example_message
  // It's loaded from config above (line 140-146) - NO auto-regeneration on page load
  // User can manually regenerate by clicking "Regenerate Preview" button

  // Handle discount change
  const handleDiscountChange = (value: number) => {
    setMaxDiscount(value)
  }

  // Handle offer types change
  const handleOfferTypesChange = (value: string[]) => {
    setOfferTypes(value)
  }

  // Save settings
  const handleSave = () => {
    updateConfigMutation.mutate({
      max_discount_percent: maxDiscount,
      offer_types: {
        percentage_off: offerTypes.includes('percentage_off'),
        free_shipping: offerTypes.includes('free_shipping'),
      },
    })
  }

  // Save brand voice
  const handleSaveBrandVoice = () => {
    updateConfigMutation.mutate({
      brand_voice: editingBrandVoice,
    })
    setBrandVoice(editingBrandVoice)
    setShowBrandVoiceModal(false)
  }

  // Open brand voice modal
  const openBrandVoiceModal = () => {
    setEditingBrandVoice({ ...brandVoice })
    setShowBrandVoiceModal(true)
  }

  // Update editing brand voice field
  const updateEditingField = (field: string, value: string) => {
    setEditingBrandVoice(prev => ({ ...prev, [field]: value }))
  }

  // Log state for debugging
  console.log('[Dashboard] isPreviewMode:', isPreviewMode, 'isLoading:', isLoading, 'error:', error)

  if (isLoading) {
    return (
      <Page title="Dashboard">
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="200">
                <Text as="p">Loading configuration...</Text>
                <Text as="p" tone="subdued">
                  Authenticating with Shopify and fetching shop settings.
                </Text>
                <Text as="p" tone="subdued">
                  Check browser console (F12) for debug logs if this takes too long.
                </Text>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  // Don't show error if we have mock/placeholder data
  if (error && !config) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    console.error('[Dashboard] Error loading config:', error)
    return (
      <Page title="Dashboard">
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <BlockStack gap="200">
                <Text as="p" fontWeight="bold">Failed to load configuration</Text>
                <Text as="p">{errorMessage}</Text>
                <Text as="p" tone="subdued">
                  Check browser console (F12) for more details.
                </Text>
              </BlockStack>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  return (
    <Page
      title="Dashboard"
      primaryAction={{
        content: 'Save Changes',
        onAction: handleSave,
        loading: updateConfigMutation.isPending,
      }}
    >
      <Layout>
        {/* Welcome Banner for First-Time Users */}
        {showWelcome && (
          <Layout.Section>
            <div style={{
              display: 'flex',
              gap: '24px',
              padding: '24px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: '1px solid #e1e3e5',
            }}>
              {/* Left: Copy */}
              <div style={{ flex: '1 1 60%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Text as="h2" variant="headingLg" fontWeight="bold">
                  Welcome to Otty AI!
                </Text>
                <div style={{ marginTop: '8px', marginBottom: '16px' }}>
                  <Text as="p" tone="subdued">
                    Your personal profit assistant. Otty recovers lost sales by sending AI-crafted, brand-matched messages with dynamic discounts — automatically.
                  </Text>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#ff6b35', fontWeight: 600 }}>Smart Discounts</span>
                    <Text as="span" tone="subdued"> — Starts low, only increases if needed</Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#ff6b35', fontWeight: 600 }}>Your Brand Voice</span>
                    <Text as="span" tone="subdued"> — Every message sounds authentically you</Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#ff6b35', fontWeight: 600 }}>Automatic Recovery</span>
                    <Text as="span" tone="subdued"> — Monitor recovered revenue in real-time</Text>
                  </div>
                </div>
                <div>
                  <Button onClick={dismissWelcome}>Got it</Button>
                </div>
              </div>
              {/* Right: Video */}
              <div style={{ flex: '0 0 40%', display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '100%', aspectRatio: '16/9', borderRadius: '8px', overflow: 'hidden' }}>
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/MsFTyqsR84g"
                    title="Welcome to Otty AI"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ border: 'none' }}
                  />
                </div>
              </div>
            </div>
          </Layout.Section>
        )}

        {/* Left Column: Configuration */}
        <Layout.Section>
          <BlockStack gap="400">
            {/* Discount Settings */}
            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">
                  Maximum Discount
                </Text>
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="p" tone="subdued">
                    Set the maximum discount percentage Otty AI can offer.
                  </Text>
                  <Box width="100px">
                    <Select
                      label=""
                      options={[
                        { label: '5%', value: '5' },
                        { label: '10%', value: '10' },
                        { label: '15%', value: '15' },
                        { label: '20%', value: '20' },
                        { label: '25%', value: '25' },
                        { label: '30%', value: '30' },
                        { label: '35%', value: '35' },
                        { label: '40%', value: '40' },
                        { label: '45%', value: '45' },
                        { label: '50%', value: '50' },
                      ]}
                      value={String(maxDiscount)}
                      onChange={(value) => handleDiscountChange(parseInt(value))}
                    />
                  </Box>
                </InlineStack>
              </BlockStack>
            </Card>

            {/* Offer Types */}
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  Offer Types
                </Text>
                <Text as="p" tone="subdued">
                  Choose which types of offers Otty AI can use to win back customers.
                </Text>
                <BlockStack gap="200">
                  {/* Percentage Off Toggle */}
                  <div
                    onClick={() => {
                      const newTypes = offerTypes.includes('percentage_off')
                        ? offerTypes.filter(t => t !== 'percentage_off')
                        : [...offerTypes, 'percentage_off']
                      handleOfferTypesChange(newTypes)
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      border: '1px solid #e1e3e5',
                    }}
                  >
                    <InlineStack gap="300" blockAlign="center">
                      <img src="/icons/discount-star-01.png" alt="Discount" style={{ width: '20px', height: '20px' }} />
                      <Text as="p" fontWeight="medium">Percentage Off</Text>
                    </InlineStack>
                    <div style={{
                      width: '40px',
                      height: '22px',
                      backgroundColor: offerTypes.includes('percentage_off') ? '#ff6b35' : '#c9cccf',
                      borderRadius: '11px',
                      position: 'relative',
                      transition: 'background-color 0.2s',
                    }}>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        backgroundColor: '#fff',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '2px',
                        left: offerTypes.includes('percentage_off') ? '20px' : '2px',
                        transition: 'left 0.2s',
                      }} />
                    </div>
                  </div>

                  {/* Free Shipping Toggle */}
                  <div
                    onClick={() => {
                      const newTypes = offerTypes.includes('free_shipping')
                        ? offerTypes.filter(t => t !== 'free_shipping')
                        : [...offerTypes, 'free_shipping']
                      handleOfferTypesChange(newTypes)
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      border: '1px solid #e1e3e5',
                    }}
                  >
                    <InlineStack gap="300" blockAlign="center">
                      <img src="/icons/delivery.png" alt="Shipping" style={{ width: '20px', height: '20px' }} />
                      <Text as="p" fontWeight="medium">Free Shipping</Text>
                    </InlineStack>
                    <div style={{
                      width: '40px',
                      height: '22px',
                      backgroundColor: offerTypes.includes('free_shipping') ? '#ff6b35' : '#c9cccf',
                      borderRadius: '11px',
                      position: 'relative',
                      transition: 'background-color 0.2s',
                    }}>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        backgroundColor: '#fff',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '2px',
                        left: offerTypes.includes('free_shipping') ? '20px' : '2px',
                        transition: 'left 0.2s',
                      }} />
                    </div>
                  </div>
                </BlockStack>
              </BlockStack>
            </Card>

            {/* Brand Voice */}
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <InlineStack gap="200" blockAlign="center">
                    <img src="/icons/annotation-typing.png" alt="Brand Voice" style={{ width: '20px', height: '20px' }} />
                    <Text as="h2" variant="headingMd">
                      Brand Voice
                    </Text>
                  </InlineStack>
                  <Button onClick={openBrandVoiceModal} variant="tertiary">
                    Edit
                  </Button>
                </InlineStack>
                <Text as="p" tone="subdued">
                  Otty analyzes your brand's unique voice and tone to craft personalized messages that sound authentically you.
                </Text>

                {/* Summary */}
                {brandVoice.summary && (
                  <div style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '2px solid #ff6b35',
                    backgroundColor: '#fff8f5',
                  }}>
                    <Text as="p" variant="bodyMd">{brandVoice.summary}</Text>
                  </div>
                )}

                {/* Details Grid */}
                <BlockStack gap="300">
                  {brandVoice.tone && (
                    <>
                      <BlockStack gap="100">
                        <Text as="p" variant="bodySm" fontWeight="semibold">Tone</Text>
                        <Text as="p" variant="bodySm" tone="subdued">{brandVoice.tone}</Text>
                      </BlockStack>
                      <Divider />
                    </>
                  )}

                  {brandVoice.personality && (
                    <>
                      <BlockStack gap="100">
                        <Text as="p" variant="bodySm" fontWeight="semibold">Personality</Text>
                        <Text as="p" variant="bodySm" tone="subdued">{brandVoice.personality}</Text>
                      </BlockStack>
                      <Divider />
                    </>
                  )}

                  {brandVoice.writingStyle && (
                    <>
                      <BlockStack gap="100">
                        <Text as="p" variant="bodySm" fontWeight="semibold">Writing Style</Text>
                        <Text as="p" variant="bodySm" tone="subdued">{brandVoice.writingStyle}</Text>
                      </BlockStack>
                      <Divider />
                    </>
                  )}

                  {brandVoice.messagingGuidelines && (
                    <>
                      <BlockStack gap="100">
                        <Text as="p" variant="bodySm" fontWeight="semibold">Messaging Guidelines</Text>
                        <Text as="p" variant="bodySm" tone="subdued">{brandVoice.messagingGuidelines}</Text>
                      </BlockStack>
                      <Divider />
                    </>
                  )}

                  {brandVoice.keywordsToUse && (
                    <>
                      <BlockStack gap="100">
                        <Text as="p" variant="bodySm" fontWeight="semibold">Keywords to Use</Text>
                        <Text as="p" variant="bodySm" tone="subdued">{brandVoice.keywordsToUse}</Text>
                      </BlockStack>
                      <Divider />
                    </>
                  )}

                  {brandVoice.avoidPatterns && (
                    <BlockStack gap="100">
                      <Text as="p" variant="bodySm" fontWeight="semibold">Avoid</Text>
                      <Text as="p" variant="bodySm" tone="subdued">{brandVoice.avoidPatterns}</Text>
                    </BlockStack>
                  )}
                </BlockStack>

                {!brandVoice.summary && !brandVoice.tone && (
                  <Box background="bg-surface-secondary" padding="400" borderRadius="200">
                    <Text as="p" variant="bodyMd" tone="subdued">
                      No brand voice configured. Click Edit to set one.
                    </Text>
                  </Box>
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>

        {/* Right Column: iPhone Preview */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Message Preview
              </Text>
              <Text as="p" tone="subdued">
                This is what your customers will see.
              </Text>
              <Divider />
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <SMSMockup
                  shopName={config?.shop_name || config?.brand_voice_tone || 'Your Shop'}
                  message={preview?.message || 'Configure your settings to see a preview...'}
                  loading={previewMutation.isPending}
                />
              </div>
              <Button onClick={generatePreview} variant="secondary" fullWidth>
                Regenerate Preview
              </Button>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Brand Voice Edit Modal */}
      <Modal
        open={showBrandVoiceModal}
        onClose={() => setShowBrandVoiceModal(false)}
        title="Edit Brand Voice"
        primaryAction={{
          content: 'Save',
          onAction: handleSaveBrandVoice,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowBrandVoiceModal(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Brand Summary"
              value={editingBrandVoice.summary}
              onChange={(value) => updateEditingField('summary', value)}
              multiline={3}
              autoComplete="off"
              placeholder="Describe your brand's overall voice and personality..."
            />
            <TextField
              label="Tone"
              value={editingBrandVoice.tone}
              onChange={(value) => updateEditingField('tone', value)}
              multiline={2}
              autoComplete="off"
              placeholder="e.g., Warm, friendly, professional..."
            />
            <TextField
              label="Personality"
              value={editingBrandVoice.personality}
              onChange={(value) => updateEditingField('personality', value)}
              multiline={2}
              autoComplete="off"
              placeholder="e.g., Helpful, knowledgeable, approachable..."
            />
            <TextField
              label="Writing Style"
              value={editingBrandVoice.writingStyle}
              onChange={(value) => updateEditingField('writingStyle', value)}
              multiline={2}
              autoComplete="off"
              placeholder="e.g., Conversational, uses emojis sparingly..."
            />
            <TextField
              label="Messaging Guidelines"
              value={editingBrandVoice.messagingGuidelines}
              onChange={(value) => updateEditingField('messagingGuidelines', value)}
              multiline={2}
              autoComplete="off"
              placeholder="e.g., Keep messages short, always include CTA..."
            />
            <TextField
              label="Keywords to Use"
              value={editingBrandVoice.keywordsToUse}
              onChange={(value) => updateEditingField('keywordsToUse', value)}
              autoComplete="off"
              placeholder="e.g., exclusive, special, treat yourself..."
            />
            <TextField
              label="Patterns to Avoid"
              value={editingBrandVoice.avoidPatterns}
              onChange={(value) => updateEditingField('avoidPatterns', value)}
              autoComplete="off"
              placeholder="e.g., URGENT, limited time, pushy language..."
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* SMS Setup Modal - shown once before merchant can use the app */}
      <Modal
        open={showSmsSetup}
        onClose={() => {}}
        title={smsSetupStep === 1
          ? "Before we start — Quick Setup (1/4)"
          : smsSetupStep === 2
          ? "Quick Setup (2/4)"
          : smsSetupStep === 3
          ? "Quick Setup (3/4)"
          : "Quick Setup (4/4)"
        }
        primaryAction={smsSetupStep < 4
          ? { content: 'Next', onAction: () => setSmsSetupStep(s => s + 1) }
          : { content: "I've done it — Let's go!", onAction: dismissSmsSetup }
        }
        secondaryActions={smsSetupStep > 1
          ? [{ content: 'Back', onAction: () => setSmsSetupStep(s => s - 1) }]
          : []
        }
      >
        <Modal.Section>
          {smsSetupStep === 1 && (
            <BlockStack gap="400">
              <Text as="p" variant="bodyMd">
                Otty recovers abandoned carts by sending personalized SMS messages to your customers.
                For this to work, <Text as="span" fontWeight="bold">customers need to provide their phone number at checkout.</Text>
              </Text>
              <Text as="p" variant="bodyMd">
                This takes about 30 seconds — just three quick settings in your Shopify admin.
              </Text>
              {checkoutSettingsUrl && (
                <div>
                  <Button url={checkoutSettingsUrl} target="_blank">
                    Open Checkout Settings
                  </Button>
                </div>
              )}
              <Banner tone="info">
                <Text as="p" variant="bodySm">
                  Without this, Otty won't have phone numbers to send recovery messages to.
                </Text>
              </Banner>
            </BlockStack>
          )}

          {smsSetupStep === 2 && (
            <BlockStack gap="400">
              <Text as="h3" variant="headingSm">
                Step 1: Set customer contact method
              </Text>
              <Text as="p" variant="bodyMd">
                Under <Text as="span" fontWeight="bold">"Customer contact method"</Text>, select <Text as="span" fontWeight="bold">"Phone number or email"</Text>.
              </Text>
              <div style={{
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid #e1e3e5',
              }}>
                <img
                  src="/step-1.png"
                  alt="Shopify checkout settings - customer contact method"
                  style={{ width: '100%', display: 'block' }}
                />
              </div>
            </BlockStack>
          )}

          {smsSetupStep === 3 && (
            <BlockStack gap="400">
              <Text as="h3" variant="headingSm">
                Step 2: Require phone number
              </Text>
              <Text as="p" variant="bodyMd">
                Under <Text as="span" fontWeight="bold">"Customer information"</Text>, set <Text as="span" fontWeight="bold">"Shipping address phone number"</Text> to <Text as="span" fontWeight="bold">"Required"</Text>.
              </Text>
              <div style={{
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid #e1e3e5',
              }}>
                <img
                  src="/step-2.png"
                  alt="Shopify checkout settings - require phone number"
                  style={{ width: '100%', display: 'block' }}
                />
              </div>
            </BlockStack>
          )}

          {smsSetupStep === 4 && (
            <BlockStack gap="400">
              <Text as="h3" variant="headingSm">
                Step 3: Turn on SMS marketing
              </Text>
              <Text as="p" variant="bodyMd">
                Under <Text as="span" fontWeight="bold">"Marketing options"</Text>, turn on <Text as="span" fontWeight="bold">"SMS"</Text>.
              </Text>
              <Text as="p" variant="bodyMd">
                This lets customers opt in to receiving messages at checkout.
              </Text>
              <div style={{
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid #e1e3e5',
              }}>
                <img
                  src="/step-3.png"
                  alt="Shopify checkout settings - SMS marketing"
                  style={{ width: '100%', display: 'block' }}
                />
              </div>
              <Banner tone="success">
                <Text as="p" variant="bodySm">
                  That's it! Once saved, Otty will automatically start recovering your abandoned carts.
                </Text>
              </Banner>
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  )
}
